/**
 * The startup benchmark's reference: what the pose server says each of the
 * four shipped frames contains (spec §4.1). Produced offline at /dev/benchmark
 * and shipped as `public/benchmark/v1/reference.json`.
 */
import type { PersonBox } from "./personBox";
import { MOVENET_MODEL_URL, POSE_DETECTION_VERSION } from "./modelInfo";

export interface ReferenceKeypoint {
  name: string;
  x: number;
  y: number;
  score: number;
}

export interface ReferenceFrame {
  file: string;
  sha256: string;
  width: number;
  height: number;
  /** 17, from the pose server. */
  keypoints: ReferenceKeypoint[];
  /** From a healthy device's height model — the server does not run one. */
  personBox: PersonBox | null;
}

export interface CalibrationRow {
  device: string;
  recordedAt: string;
  backend: string;
  float32Enabled: boolean | null;
  cycleMedianMs: number;
  cycleMaxMs: number;
  gatedDevMaxRatio: number;
  confidenceRatioMin: number;
}

export interface BenchmarkReference {
  version: 1;
  generatedAt: string;
  model: { name: string; url: string; poseDetectionVersion: string };
  server: { service: string; revision: string };
  procedure: string;
  frames: ReferenceFrame[];
  calibration: CalibrationRow[];
}

/** Versioned, never edited in place — a new reference is `v2/`. */
export const BENCHMARK_BASE_PATH = "/benchmark/v1/";
export const BENCHMARK_FRAME_COUNT = 4;
export const BENCHMARK_FRAME_WIDTH = 480;
export const BENCHMARK_FRAME_HEIGHT = 640;

export type ReferenceCheck =
  | { ok: true; reference: BenchmarkReference }
  | { ok: false; reason: "reference-invalid" | "reference-stale" };

export function validateReference(
  raw: unknown,
  expected: { modelUrl: string; poseDetectionVersion: string } = {
    modelUrl: MOVENET_MODEL_URL,
    poseDetectionVersion: POSE_DETECTION_VERSION,
  }
): ReferenceCheck {
  if (!raw || typeof raw !== "object") return { ok: false, reason: "reference-invalid" };
  const r = raw as Partial<BenchmarkReference>;
  if (r.version !== 1 || !r.model || !Array.isArray(r.frames)) {
    return { ok: false, reason: "reference-invalid" };
  }
  if (r.frames.length !== BENCHMARK_FRAME_COUNT) return { ok: false, reason: "reference-invalid" };
  for (let i = 0; i < r.frames.length; i++) {
    const f = r.frames[i];
    if (
      !f ||
      typeof f.file !== "string" ||
      !Array.isArray(f.keypoints) ||
      f.keypoints.length !== 17 ||
      f.width !== BENCHMARK_FRAME_WIDTH ||
      f.height !== BENCHMARK_FRAME_HEIGHT
    ) {
      return { ok: false, reason: "reference-invalid" };
    }
  }
  if (
    r.model.url !== expected.modelUrl ||
    r.model.poseDetectionVersion !== expected.poseDetectionVersion
  ) {
    return { ok: false, reason: "reference-stale" };
  }
  return { ok: true, reference: r as BenchmarkReference };
}
